import React from 'react';
import ReactGA from 'react-ga4';
import cx from 'classnames';
import cloneDeep from 'lodash.clonedeep';
import get from 'lodash.get';
import TextInput from '../Student/AssessmentItems/TextInput';
import ReadAloud from '../Student/AssessmentItems/ReadAloud';
import ReadAloudVideo from '../Student/AssessmentItems/ReadAloudVideo';
import RANItems from '../Student/Dyslexia/RANItems';
import SingleWord from '../Student/Items/SingleWord';
import SplitItem from '../Student/Dyslexia/SplitItem';
import Video from '../Student/Dyslexia/Video';
import {findFilenames, getUserData} from "../../services/API";
import {COPY_STRINGS} from '../../constants/translatedCopy';
import {NO_RESPONSE, phraseTypes} from '../../constants/constants';
import {parseSkipTranslationTags, parseTextWithPhonemes} from "../../services/util";
import Quiz from '../Quiz/Quiz';
import Transition from './Transition';
import PictureStoryPage from '../Student/Practice/PictureStoryPage';
import StoryIntro from './StoryIntro';
import WordPartDeletion from './Phrase/PhraseActivities/WordPartDeletion/index';
import ChangeOnePartWrapper from './Phrase/PhraseActivities/ChangeOnePart/ChangeOnePartWrapper';
import UpAndDownTutor from './Phrase/PhraseActivities/UpAndDown/UpAndDownTutor';
import ElkoninTutor from './Phrase/PhraseActivities/Elkonin/ElkoninTutor';
import NonsenseWordRepetition from './Phrase/PhraseActivities/NonsenseWordRepetition/NonsenseWordRepetition';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";
import LetterFliesWrapper from './Phrase/PhraseActivities/LetterFlies/LetteryfliesWrapper';
import { withCurrentStoryWordMetadata } from '../../context/CurrentStoryMetadataContext';

const statusOptions = {
  ERROR: "error",
  SUCCESS: "success",
  UNREAD: "unread",
  PROCESSING: "processing",
  INTERVENTION: "intervention",
  FLAGGED: "flagged",
  BOUNDARY_WORD: "boundaryWord",
}

// for phrase types in this array we will ignore veerbal instructions in the phrase metadata
//  if we're playing the FTUE experience
const IGNORE_INSTRUCTIONS_FTUE_TYPES = [
  phraseTypes.UP_AND_DOWN,
  phraseTypes.CHANGE_ONE_PART,
  phraseTypes.ELKONIN,
]

// this is a way to temporarily disable phrase level instructions entirely for activity types
// which don't support this yet but intend to in the future
const IGNORE_INSTRUCTIONS_ENTIRELY_TYPES = [
  phraseTypes.ELKONIN,
]

const IGNORE_INSTRUCTIONS_NON_FTUE_TYPES = [
  phraseTypes.LETTER_FLIES,
]

let itemTypeHistory = [];

function checkPhraseFtue(types) {
  if(!types){
    return false;
  }
  for (const type of itemTypeHistory) {
    if (types.includes(type)) return false;
  }
  return true;
}

class Word extends React.Component {
  constructor (props){
    super(props);
    this.handleClick = this.handleClick.bind(this);
  }

  handleClick(){
    // do nothing if the word is not clickable
    if(!this.props.clickable){
      return;
    }

    //if the word is clickable and we have a setPlacement function, we are in the reading tutor context
    //  and should set the placement. Otherwise, we are in the correction context and should toggle the status
    // TODO dis-entangle props for setPlacement and toggling status
    if(this.props.setPlacement){
      //this.props.setPlacement(this.props.index);
      //do nothing
      // TODO: re-enable click placement, both here and within the definition of "isClickable",
      // within the getMarkedPhrase method
    } else {

      // toggle between "error", "success", and "unread" for edge words,
      //  and "error" and "success" for all other words
      let newStatus = "";
      if(this.props.status === statusOptions.ERROR){
        if(this.props.canGrayOut(this.props.phraseIndex, this.props.index)){
          newStatus = statusOptions.UNREAD;
        } else {
          newStatus = statusOptions.SUCCESS;
        }
      } else if(this.props.status === statusOptions.SUCCESS){
        newStatus = statusOptions.ERROR;
      } else {
        newStatus = statusOptions.SUCCESS;
      }
      // Log this click to google analytics
      this.logCorrection(newStatus);

      this.props.updateErrors(this.props.index, newStatus);
    }
  }

  // log the correction to google analytics for usage tracking
  logCorrection(status) {
    ReactGA.event({
      category: 'Activity Page Action',
      action: 'teacher corrected a word to status ' + status,
      label: this.props.word,
    });
  }

  // returns a function which triggers a toggle on an example word.
  // To be used in in context help, currently dead code
  getDemoClickFunction(){
    return function () {
      this.toggleStatus();
      window.setTimeout(this.toggleStatus, 3000);
    }
  }

  render(){
    let wordClass = "word " + this.props.status + this.props.className;
    if(this.props.highlighted){
      wordClass += " highlighted";
    }
    if(this.props.clickable){
      wordClass += " clickable";
    }
    if(this.props.isEarlyReader && this.props.isEarlyReader()) {
      wordClass += " earlyReader";
    }

    const accessibleClickAttributes = this.props.clickable ?
      {...doNotUseAccessibleStaticElementInteractions({onClick: this.handleClick})} : {};

    // add special selector to 6th word for in context help
    return (<span className={wordClass} data-tut={this.props.index == 6 ? "word" : null} {...accessibleClickAttributes}>{this.props.word}</span>);
    /*if(this.props.index == 6){
      if(this.props.setDemoClick){
        this.props.setDemoClick(this.getDemoClickFunction().bind(this));
      }
    }*/
  }
}

class Phrase extends React.Component {
  constructor (props){
    super(props);
    const introduction = this.isSessionFirstActivity() ? get(this.props, 'phraseMetaData.introductions.textIntroductions.full') : get(this.props, 'phraseMetaData.introductions.textIntroductions.short')
    const startPhraseDisplay = introduction || (!this.shouldIgnoreInstructions() && get(this.props, 'phraseMetaData.instructions.text')) || this.props.text

    this.state = {
      text: startPhraseDisplay,
      metaData: this.parseMetaData(this.props.phraseMetaData),
      willHandlePause: false,
    }
    this.updateErrors = this.updateErrors.bind(this);
    this.handleStartOfPhrase = this.handleStartOfPhrase.bind(this);
    this.isSessionFirstActivity = this.isSessionFirstActivity.bind(this);
    this.speakWithPhoneme = this.speakWithHumanPhoneme.bind(this);
    this.speakPhonemes = this.speakHumanPhonemes.bind(this);
    this.checkSetPreviousPhraseErrors = this.checkSetPreviousPhraseErrors.bind(this);
    this.setWillHandlePause = this.setWillHandlePause.bind(this);
  }

  componentDidMount(){
    if(this.props.isActive){
      this.handleStartOfPhrase()
    }
  }

  componentDidUpdate(prevProps){

    //For pages that have multiple phrases per page, hit the start of phrase stuff when they become active.
    if(!prevProps.isActive && this.props.isActive){
      this.handleStartOfPhrase();
    }
    // if prev props's video url is different than the current prop's videoURL OR
    // prev props's phraseMetadata.assetURL is different than the current prop's assetURL
    // AND if the new URL is not null
    if(prevProps.phraseMetaData && this.props.phraseMetaData){
      if((this.props.phraseMetaData.videoURL && (prevProps.phraseMetaData.videoURL != this.props.phraseMetaData.videoURL))  ||
      (this.props.phraseMetaData.assetURL && (prevProps.phraseMetaData.assetURL != this.props.phraseMetaData.assetURL))){
        this.setState({
          text: this.props.text,
          metaData: this.parseMetaData(this.props.phraseMetaData),
        }, this.handleStartOfPhrase.bind(this));
      }
    }

    // pause detection handling for all phrases
    if(this.props.isActive){
      if(prevProps.paused !== this.props.paused){
        if(this.props.paused){
          if(!this.state.willHandlePause){
            const interventionInProgress = this.props.handleDefaultPauseDetected();
            if(!interventionInProgress){
              console.log("🔵 calling done handling pause")
              this.props.doneHandlingPause();
            }
          }else{
            // phrase item component should call doneHandlingPause to tell reader manager that it's done handling the pause
            console.log("🔵 component will handle pause");
          }
        }else{
          // resumed
        }
      }
    }
  }

  componentWillUnmount() {
    // store the item type so that we can know for instructional purposes whether the child has seen this item type before
    itemTypeHistory.push(this.state.metaData.type);
  }

  setWillHandlePause(willHandlePause){
    this.setState({ willHandlePause });
  }

  shouldIgnoreInstructions() {
    const { phraseMetaData } = this.props;
  
    if (!phraseMetaData) {
      return false;
    }

    const { type, playFTUE } = phraseMetaData;
    const isFTUE = checkPhraseFtue(type) || playFTUE;
    const ignore_ftue_instructions = IGNORE_INSTRUCTIONS_FTUE_TYPES.includes(type);
    const ignore_entire_instructions = IGNORE_INSTRUCTIONS_ENTIRELY_TYPES.includes(type);
    const ignore_non_ftue_instructions = IGNORE_INSTRUCTIONS_NON_FTUE_TYPES.includes(type);

    if(ignore_entire_instructions){
      return true;
    }
    if (isFTUE && ignore_ftue_instructions) {
      return true;
    }
    if (!isFTUE && ignore_non_ftue_instructions) {
      return true;
    }
  
    return false;
  }

  parseMetaData(phraseMetaData){
    let metaData = cloneDeep(phraseMetaData) || {};

    // set the metadata for current render
    metaData = metaData.introductions || metaData.instructions || metaData;
    metaData.isVideo = metaData.videoURL || metaData.type === "readAloudVideo";

    if(metaData.isVideo && getUserData().sessionData.useIpadFallbacks){
      let {verbalInstructions=""} = metaData;
      metaData.verbalInstructions = `${verbalInstructions} ${COPY_STRINGS.intervention_strings.tap_play}`;
    }

    return metaData;
  }

  speakVerbalInstructions(verbalInstructions, advanceMetaData){
    // a callback for on end of speech to call
    let onEnd = () => {
      // Only hit the startPhrase callback if there is no videoURL.
      // Otherwise let the video take care of it when it's done playing.
      if(this.state.metaData.isVideo && this.state.metaData.type !== "readAloudVideo"){
        advanceMetaData();
      }else if(this.state.metaData.textIntroductions){
        advanceMetaData();
      }else if(this.state.metaData.transition){
        this.startTransition();
      }else {
        if(this.shouldAutoStartPhrase(this.props.phraseMetaData.type)){
          this.props.startPhrase();
        }
        advanceMetaData();
      }
    }

    // if we're ignoring verbal instructions, just call onEnd and return without speaking
    //  this will happen if we're playing the FTUE for a phrase type
    //  which skips verbal instructions on FTUE
    if(this.shouldIgnoreInstructions()){
      onEnd();
      return;
    }

    if(getUserData().bilingualMode){
      let parsedInstructions = parseSkipTranslationTags(verbalInstructions);
      if(this.props.skipIntros) parsedInstructions = [{textToSpeak: "Skipping Instructions"}];
      let bilingualSequenceToSpeak = {
        textToSpeak: parsedInstructions,
        passThroughSpeechTags: true,
        onEnd: onEnd,
      }

      // bilingual mode uses speakSequence, takes broken up speech strings as an array of strings
      // some instructions has a section of English text to be spoken in English.
      // using speakSqeuence, it will speak using the voice based on the language of the text.
      this.props.speech.speakSequence(bilingualSequenceToSpeak);
    } else {

      // parse the instruction, break into an array of objects with normal text and phoneme text
      const speechItemsWithHumanPhonemes = parseTextWithPhonemes(verbalInstructions)

      // this speak with human phoneme is here to support English for now, when we are ready to
      // suppport Spanish, refactor this with the above bilingualMode section
      if(speechItemsWithHumanPhonemes.length){
        this.speakWithHumanPhoneme(speechItemsWithHumanPhonemes, onEnd)
      }else if(verbalInstructions){
        if(this.props.skipIntros) verbalInstructions = "Skipping Instructions";
        let instructionSpeech = {
          textToSpeak: verbalInstructions,
          passThroughSpeechTags: true,
          onEnd: onEnd,
        }
        // passing empty string to .speak will cause a fatal error
        // What if we use speakSequence here?
        this.props.speech.speak(instructionSpeech);
      }else{
        // catch cases where an empty string is passed, it should still
        // call onEnd to advance the metadata to continue the story.
        onEnd()
      }
    }
  }

  /**
   * Takes a list of speech items with human spoken phonemes and call speech.speak on it if it's text string,
   * If it is a phoneme string, it gets the audio files and pass to speakPhonemes to play the audio.
   * @param {Array} speechItems An array of parsed speech item objects { type, value }
   * @param {Function} speakCallback A callback function which will be called after finishing speaking the speech items
   * @returns nothing
   */
  speakWithHumanPhoneme(speechItems, speakCallback){

    if(!speechItems.length){
      speakCallback()
      return
    }

    const item = speechItems.shift()
    const onEnd = () => {
      this.speakWithHumanPhoneme(speechItems, speakCallback)
    }

    // use amira speak if it's text
    if(item.type === 'text'){
      this.props.speech.speak({
        textToSpeak: item.value,
        passThroughSpeechTags: true,
        onEnd
      })
    }

    // use human recorded voice if it's a phoneme
    if(item.type === 'phoneme'){
      const urls = findFilenames(item.value)
      // if no urls found, just move to the next speech item
      if(!urls.length) onEnd()
      // speak all phoneme audios, then move to the next speech item
      this.speakHumanPhonemes(urls, onEnd)
    }
  }

  /**
   * Takes a list of human spoken phoneme urls and play it
   * @param {Array.<string>} phonemeURLs An array of phoneme URLs
   * @param {Function} speakCallback A callback function which will be called after finishing playing the list of audio files
   * @returns nothing
   */
  speakHumanPhonemes(phonemeURLs, speakCallback){

    if(!phonemeURLs.length){
      speakCallback()
      return
    }

    const onEnd = () => {
      this.speakHumanPhonemes(phonemeURLs, speakCallback)
      return
    }
    const url = phonemeURLs.shift()
    this.props.speech.speakAudioFile({
      url,
      onfinish: onEnd
    })
  }

  startTransition() {
    let state = this.state;
    state.metaData.type = "transition";
    this.setState(state);
  }

  onTransitionEnd(){
    this.setState({
      text: this.props.text,
      metaData: this.props.phraseMetaData,
    });
    this.props.startPhrase();
  }

  /**
   * All phrase types except for the ones listed below should auto start
   * @param {String} type
   * @returns
   */
  shouldAutoStartPhrase(type){
    return ![
      phraseTypes.WORD_PART_DELETION,
      phraseTypes.NONSENSE_WORD_REPETITION,
      phraseTypes.CHANGE_ONE_PART,
      phraseTypes.LETTER_FLIES,
      phraseTypes.ELKONIN
    ].includes(type);
  }

  handleStartOfPhrase(){
    const hasIntroductions = get(this.state, 'metaData.verbalIntroductions');
    //If we have no introductions AND no verbal instructions AND no video to play, then hit the startPhrase callback
    if(!this.state.metaData || (!hasIntroductions && !this.state.metaData.verbalInstructions && !this.state.metaData.transition && (!this.state.metaData.isVideo || this.state.metaData.type === "readAloudVideo"))){
      if(this.shouldAutoStartPhrase(this.state.metaData.type) && this.props.startPhrase){
        this.props.startPhrase();
      }
    }else{
      // present introduction first.
      if (this.state.metaData.verbalIntroductions){
        const verbalIntroduction = this.isSessionFirstActivity() ? get(this.state, 'metaData.verbalIntroductions.full') : get(this.state, 'metaData.verbalIntroductions.short')
        this.speakVerbalInstructions(verbalIntroduction, ()=> {
          // callback logic to remove the introduction just presented from metadata
          // replace with instructions if any, otherwise, replace with story text
          const withoutIntroductions = { ...this.props.phraseMetaData }
          const hasInstructions = get(this.props, 'phraseMetaData.instructions')
          delete withoutIntroductions.introductions
          const text = (hasInstructions && !this.shouldIgnoreInstructions()) ? get(this.props, 'phraseMetaData.instructions.text') : this.props.text
          this.setState({
            text: text,
            metaData: this.parseMetaData(withoutIntroductions), // update state.metadata to instructions metadata
          }, this.handleStartOfPhrase);
        });
      // then present instructions.
      }else if(this.state.metaData.verbalInstructions){
        this.speakVerbalInstructions(this.state.metaData.verbalInstructions, ()=> {
          // callback logic to remove instructions after repsenting from metadata
          // replace with story text
          this.setState({
            text: this.props.text,
            metaData: this.props.phraseMetaData, // update state.metadata to of the actual phrase item metadata
          });
        });
      }
      else if (this.state.metaData.transition){
        this.startTransition();
      }
    }
  }

  // keep track of the new set of errors so that we can save teachers' changes when we are in correction mode
  // this will never be called when we are in tutor mode
  updateErrors(index, newStatus){
    let newErrors = this.props.errors;
    if(index < newErrors.length && newStatus === statusOptions.UNREAD){
      newErrors = newErrors.slice(0, index); // remove all errors after this index
    }
    // if word is unread and being set to read, mark as read all previous unread words
    else if(index > newErrors.length-1){
      let count = index - newErrors.length;
      for(let i = 0; i <= count; i++){
        newErrors.push(false);
      }
      let prevUnreadCheck = false;
      let i = 1;
      while(!prevUnreadCheck){
        prevUnreadCheck = this.checkSetPreviousPhraseErrors(this.props.phraseIndex - i);
        i += 1;
      }
    }else {
      newErrors[index] = (newStatus === statusOptions.ERROR);
    }

    let newFlags = this.props.flaggedWords;
    if(newFlags){
      newFlags[index] = false;
    }

    this.props.setErrors(newErrors,newFlags,this.props.phraseIndex);
  }

  checkSetPreviousPhraseErrors(phraseIndex){
    if(phraseIndex < 0) return true;
    let phraseLength = this.props.phrases[phraseIndex].text.split(' ').filter(word => word != "" && word != " ").length;
    let errors = this.props.storyErrors[phraseIndex];
    if(phraseLength === errors.length) return true;
    else{
      let newErrors = errors.slice(0);
      let count = phraseLength - errors.length;
      for(let i = 0; i < count; i++){
        newErrors.push(false);
      }
      this.props.setErrors(newErrors, null, phraseIndex);
      return false;
    }
  }

  //wrap each word in a span with the appropriate classes to style it
  getMarkedPhrase(){
    let words = this.state.text.split(' ').filter(word => word != "" && word != " ");
    let markedPhrase = [];
    for(let i = 0; i < words.length; i++){
      let wordStatus = statusOptions.UNREAD;
      if(this.props.phraseWordStylingEnabled){ //disable word status styling in assessment mode
        if(this.props.interventionWordIndex == i){
          wordStatus = statusOptions.INTERVENTION;
        } else if(this.props.errors[i]){
          if(this.props.final){
            wordStatus = statusOptions.ERROR;
          } else {
            wordStatus = statusOptions.PROCESSING;
          }
        } else {
          if (i < this.props.errors.length ){
            wordStatus = statusOptions.SUCCESS;
          }
        }

        //flagged status overrides all other statuses
        if(this.props.flaggedWords && this.props.flaggedWords[i]){
          wordStatus = statusOptions.FLAGGED;
        }
      }

      // Currently, the word is only clickable if we are in the teacher app,
      // and the word is either a scored word or an unread boundary word.
      // We will give setPlacement a value of 'true' in some student contexts
      // once we've worked through some UX concerns and placement manager issues.
      // TODO: set isClickable to true if setPlacement is true, after re-enabling placement
      let isClickable = this.props.isCorrection && wordStatus !== statusOptions.BOUNDARY_WORD && !this.props.isDisabled;

      markedPhrase.push(
        <Word
          canGrayOut={this.props.canGrayOut}
          phraseIndex={this.props.phraseIndex}
          updateErrors={this.updateErrors}
          status={wordStatus}
          highlighted={this.props.config.enableCurrentWordHighlight && i == this.props.highlightedWord}
          word={words[i]}
          key={"word"+i}
          index={i}
          clickable={isClickable}
          setPlacement={this.props.setPlacement}
          setDemoClick={this.props.setDemoClick}
          className={this.props.isCorrection ? " teacher" : ""}
          isEarlyReader={this.props.isEarlyReader}
        />
      );

      // separate with a space
      if(i < words.length - 1){
        markedPhrase.push(<span key={"space"+i}>{' '}</span>);  //TODO: think of a better way to do this
      }
    }
    return markedPhrase;
  }

  onVideoEnd(){
    console.log("in onVideoEnd");
    if(this.props.phraseMetaData.noInput){
      this.props.phraseTimeout();
    } else {
      this.props.startPhrase();
    }
  }

  // used to determine which introduction to use, full or short
  isSessionFirstActivity(){
    return this.props.activityIndex < 2
  }

  getDisplayObject(){
    const isInstruction = (this.state.metaData && (this.state.metaData.verbalInstructions || this.state.metaData.textIntroductions)) ? true : false;
    const introduction = this.isSessionFirstActivity() ? get(this.state, "metaData.textIntroductions.full") : get(this.state, "metaData.textIntroductions.short")
    switch(this.state.metaData.type){
      case("wordPartDeletion"):
        return <WordPartDeletion
          speech={this.props.speech}
          transcript={this.props.transcript}
          setWillHandlePause={this.setWillHandlePause}
          paused={this.props.paused}
          startPhrase={this.props.startPhrase}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          phraseTimeout={this.props.phraseTimeout}
          doneHandlingPause={this.props.doneHandlingPause}
          targetPart={this.props.text}
          phraseMetadata={this.props.phraseMetaData}
          isPlayingVerbalInstructions={!!this.state.metaData.verbalInstructions}
          currentPageIndex={this.props.currentPageIndex}
          />
      case("changeOnePart"):
        return <ChangeOnePartWrapper
          speech={this.props.speech}
          transcript={this.props.transcript}
          setWillHandlePause={this.setWillHandlePause}
          paused={this.props.paused}
          startPhrase={this.props.startPhrase}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          phraseTimeout={this.props.phraseTimeout}
          isFTUE={checkPhraseFtue(this.state.metaData.type) || !!this.props.phraseMetaData.playFTUE}
          doneHandlingPause={this.props.doneHandlingPause}
          originalWord={ this.props.phraseMetaData.instructions ? this.props.phraseMetaData.instructions.text : this.props.text }
          targetWord={this.props.text}
          phraseMetaData={this.props.phraseMetaData}
          isPlayingVerbalInstructions={!!this.state.metaData.verbalInstructions}
          currentPageIndex={this.props.currentPageIndex}
          isInstruction = {isInstruction}
          hasInstructions = {!!this.props.phraseMetaData.instructions && !this.shouldIgnoreInstructions()}
          currentWordIndex={this.props.currentWordIndex}
          setDisableInterventionsForPhrase={this.props.setDisableInterventionsForPhrase}
          />;
      case("upAndDown"):
        return (
          <UpAndDownTutor
            speech={this.props.speech}
            phrase={this.state.text}
            isFTUE={checkPhraseFtue(this.state.metaData.type) || !!this.props.phraseMetaData.playFTUE}
            currentWordIndex = {this.props.currentWordIndex}
            setDisableInterventionsForPhrase = {this.props.setDisableInterventionsForPhrase}
            advanceOneWord = {this.props.advanceOneWord}
            skipWord = {this.props.skipWord}
            isInstruction = {isInstruction}
            hasInstructions = {!!this.props.phraseMetaData.instructions && !this.shouldIgnoreInstructions()}
            phraseMetadata={this.props.phraseMetaData}
            story={this.props.story}
            currentPageIndex={this.props.currentPageIndex}         
            currentPhraseWordMetadata={this.props.currentPhraseWordMetadata}   
          />
        );
      case("elkonin"):
        return (
          <ElkoninTutor
            speech={this.props.speech}
            phrase={this.state.text}
            currentWordIndex = {this.props.currentWordIndex}
            isFTUE={checkPhraseFtue(this.state.metaData.type) || !!this.props.phraseMetaData.playFTUE}
            setDisableInterventionsForPhrase = {this.props.setDisableInterventionsForPhrase}
            advanceOneWord = {this.props.advanceOneWord}
            exampleWord = {this.props.phraseMetaData.instructions && this.props.phraseMetaData.instructions.text}
            startPhrase = {this.props.startPhrase}
            paused={this.props.paused}
            pausePhrase={this.props.pausePhrase}
            resumePhrase={this.props.resumePhrase}
            setWillHandlePause={this.setWillHandlePause}
            currentPhraseWordMetadata={this.props.currentPhraseWordMetadata}
          />
        );
      case("nonsenseWordRepetition"):
        return <NonsenseWordRepetition
          speech={this.props.speech}
          transcript={this.props.transcript}
          setWillHandlePause={this.setWillHandlePause}
          paused={this.props.paused}
          startPhrase={this.props.startPhrase}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          phraseTimeout={this.props.phraseTimeout}
          doneHandlingPause={this.props.doneHandlingPause}
          nonsenseWord={this.props.text}
          phraseMetadata={this.props.phraseMetaData}
          isPlayingVerbalInstructions={!!this.state.metaData.verbalInstructions}
          currentPageIndex={this.props.currentPageIndex}
          />
      case("letterFlies"):
        return <LetterFliesWrapper
          speech={this.props.speech}
          startPhrase={this.props.startPhrase}
          isFTUE={checkPhraseFtue(this.state.metaData.type) || !!this.props.phraseMetaData.playFTUE}
          currentWordIndex={this.props.currentWordIndex}
          phraseMetadata={this.props.phraseMetaData}
          isInstruction={isInstruction}
          transcript={this.props.transcript}
          setWillHandlePause={this.setWillHandlePause}
          paused={this.props.paused}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          doneHandlingPause={this.props.doneHandlingPause}
          />
      case("intro"):
        const title = this.props.story.title;
        const author = this.props.story.author;
        let subtitle;
        const hasAuthor = author && author.trim() !== "";
        if (hasAuthor) {
          subtitle = `by ${author}`;
        }

        return <StoryIntro introduction={introduction} imageURL={this.state.metaData.imageURL} title={title} subtitle={subtitle} />
      case("RAN"):
        return <RANItems image={this.state.metaData.imageURL} isInstruction={isInstruction}/>;
      case("sound"):
        let soundLetter = this.state.text;
        soundLetter = soundLetter.replace("_sound",""); //TODO: do this in a less hacky way
        let leftString = soundLetter.toUpperCase();
        let rightString = soundLetter.toLowerCase();
        return <SplitItem left={leftString} right={rightString} isInstruction={isInstruction}/>;
      case("word"):
        let wordImage = this.props.disablePictures ? null : this.state.metaData.imageURL;
        return <SingleWord
          text={this.state.text}
          image={wordImage}
          isInstruction={isInstruction}
          isEarlyReader={this.props.isEarlyReader}
          speech={this.props.speech}
          transcript={this.props.transcript}
          setWillHandlePause={this.setWillHandlePause}
          paused={this.props.paused}
          startPhrase={this.props.startPhrase}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          phraseTimeout={this.props.phraseTimeout}
          doneHandlingPause={this.props.doneHandlingPause}
          currentPageIndex={this.props.currentPageIndex}
          advanceOneWord={this.props.advanceOneWord}
          setDisableInterventionsForPhrase = {this.props.setDisableInterventionsForPhrase}
          triggerWordIntervention = {this.props.triggerWordIntervention}
        />;
      case("textInput"):
        return <TextInput  key={"textInput" + this.state.text} text={this.state.text} isInstruction={isInstruction} isRepeating={this.props.isRepeating} setTextResponse={this.props.setTextResponse} speech={this.props.speech}/>;
      case("video"):
        if((this.state.metaData.verbalInstructions) && getUserData().sessionData.isiPad){ // don't mount the video until we are done speaking on ipad to prevent volume jump
          return null;
        } else {
          return (
            <Video
              videoURL={this.state.metaData.videoURL}
              text={this.state.text}
              playVideo={!this.state.metaData.verbalInstructions}
              onDone={this.onVideoEnd.bind(this)}
              unmountWhenDone={getUserData().sessionData.isiPad && !this.props.isNextPhraseVideo}
              key="theOnlyDyslexiaVideo"
              ref="theOnlyDyslexiaVideo"
              speech={this.props.speech}
            />
          );
        }
      case("readAloud"):
        return <ReadAloud audioURL={this.state.metaData.audioURL} onAudioFinished={this.props.phraseTimeout}/>;
      case("quiz"):
        return (
          <Quiz
            doIntro={false}
            speech={this.props.speech}
            questions={this.state.metaData.questions}
            image={this.state.metaData.imageURL}
            onSubmit={(data) => {this.props.setTextResponse(data[0], true)}}
            sendMessage={this.props.sendMessage}
            submitButton={true}
          />
        );
      case("readAloudVideo"):
        if(this.state.metaData.verbalInstructions && getUserData().sessionData.isiPad){ // don't mount the video until we are done speaking on ipad to prevent volume jump
            return null;
          } else {
          return (
            <ReadAloudVideo
              assetURL={this.state.metaData.assetURL}
              onDone={() => {this.props.setTextResponse(NO_RESPONSE, true)}}
              unmountWhenDone={getUserData().sessionData.isiPad && !this.props.isNextPhraseVideo}
              speech={this.props.speech}
            />
          );
        }
      case("pictureStoryPage"):
        let imageURL = this.props.disablePictures ? null : this.state.metaData.imageURL;
        return (
          <PictureStoryPage imageURL={imageURL} phrase={this.getMarkedPhrase()}/>
        );
      case("transition"):
        return <Transition type={this.state.metaData.transition} onEnd={this.onTransitionEnd.bind(this)} speech={this.props.speech} />
      default:
        return this.getMarkedPhrase();
    }
  }

  render() {
    let markedPhrase = this.getMarkedPhrase();

    let storyLength = markedPhrase.length / 2; // divide by 2 because we are counting spaces. This is not 100% accurate since there is no trailing space, but it is close enough for our purposes

    // if this is such a long phrase that it would overflow reader, make the font smaller.
    // this will happen with longer stories in the assessment use case. For now, we will size it down a fixed amount.
    // In the future we can bring in screen size etc
    const shortStoryCutoff = 95;
    const longStoryCutoff = 170;
    const hasImage = this.state.metaData.imageURL;
    let phraseClasses = cx({
      "phrase": true,
      "pictureStory": hasImage,
      "shortStory": storyLength < shortStoryCutoff && !hasImage,
      "longStory": storyLength > longStoryCutoff && !hasImage,
      "highlightErrors": false,//process.env.STACK !== "PRODUCTION",
      [this.props.className]: this.props.className && true,
    });

    return(
      <div className={phraseClasses}>
        {this.getDisplayObject()}
      </div>
    );
  }
}

export default withCurrentStoryWordMetadata(Phrase);